import React, { FC, useEffect } from 'react';
import { Audience, AuthStates, SecureRoute, useAuthState } from '@vis-auth/react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { EveRoutes } from '@ver-uds/e-verify';
import Environment from './constants/Environment';
import App from './App';
import { IsFeatureEnabled } from './utils/IsFeatureEnabled';
import DashboardApp from './DashboardApp';
import { invalidAccountId } from './constants/Dashboard';
import { useToggles } from './context/Toggles/TogglesContext';
import MaintenanceMode from './components/MaintenanceMode/MaintenanceMode';
import { MfaMigrationWrapper } from './components/MfaMigration/MfaMigration';
import { GetAuthServiceMFA } from './utils/mfaAuthUtils';

export const OriginRedirect = ({ path }: { path: string }): JSX.Element => {
  useEffect(() => {
    window.location.assign(path);
  });
  return <></>;
};

const AppRoute: FC = () => {
  const toggles = useToggles();
  const isMaintenanceMode = toggles?.get('MAINTENANCE_MODE') === 'on';

  const { secureRouterReferrerPath, audience, user, state } = useAuthState();

  const accountId = user?.account_id as string;
  const hasValidAccount = !!accountId && accountId !== invalidAccountId;

  const isAuthed = state === AuthStates.authenticated;
  const dashNotFound = !hasValidAccount && isAuthed;

  const isEnrollmentJWT = audience === Audience.everifyEnrollment;

  const authService = GetAuthServiceMFA(EveRoutes.LOGIN_DOT_GOV_LOGIN);

  // If user completed MFA enrollment but hasn't logged out, end the enrollment session and redirect to login.gov
  useEffect(() => {
    const enrollmentCompletedTime = JSON.parse(localStorage.getItem('EnrollmentCompletedTime') || '0') as number;
    const currentTime = Date.now();
    // Trigger a logout for the enrollment session if enrollment was completed in the last 15 minutes
    const endEnrollmentSessionFlag = enrollmentCompletedTime > currentTime - 900000;
    if (isEnrollmentJWT && endEnrollmentSessionFlag) {
      localStorage.removeItem('EnrollmentCompletedTime');
      authService.logout();
    }
  }, [isEnrollmentJWT, authService]);

  return (
    <Switch>
      {isMaintenanceMode && (
        <Switch>
          <Route exact path="/maintenance">
            <MaintenanceMode />
          </Route>

          <Route path="/">
            <Redirect to="/maintenance" />
          </Route>
        </Switch>
      )}

      {/* Helper redirect for local development convenience only:
        localhost:4401 --> localhost:4401/home/login */}
      {IsFeatureEnabled([Environment.LOCAL]) && (
        <Route exact path="/">
          <OriginRedirect path={EveRoutes.LOGIN} />
        </Route>
      )}

      {!IsFeatureEnabled([Environment.LOCAL]) && (
        <Route exact path="/">
          <OriginRedirect path={EveRoutes.HOME} />
        </Route>
      )}

      {isEnrollmentJWT && (
        <Route path="/">
          <OriginRedirect path={EveRoutes.ACCOUNT_ENROLL} />
        </Route>
      )}

      {dashNotFound && (
        <Route path="/">
          <Switch>
            <Route exact path={EveRoutes.DASHBOARD_NOT_FOUND}>
              <DashboardApp canAccessDashboard={false} />
            </Route>
            <Route path="/">
              <OriginRedirect path={EveRoutes.DASHBOARD_NOT_FOUND} />
            </Route>
          </Switch>
        </Route>
      )}

      {!isEnrollmentJWT && !dashNotFound && (
        <SecureRoute path="/">
          <Switch>
            <Route exact path={EveRoutes.LOGIN}>
              {/*
                If the secureRouterReferrerPath exists, the VIS auth client will handle redirecting to the user's requested route.
                If secureRouterReferrerPath equals this path (i.e. /home/login), redirect to /home.
              */}

              {secureRouterReferrerPath && secureRouterReferrerPath.includes(EveRoutes.CASES_PREFIX) ? (
                <OriginRedirect path={secureRouterReferrerPath} />
              ) : (
                <OriginRedirect path={EveRoutes.HOME} />
              )}
            </Route>
            <Route exact path="/migrate">
              <MfaMigrationWrapper />
            </Route>
            <Route path="/dashboard">
              <DashboardApp />
            </Route>
            <Route path="/">
              <App />
            </Route>
          </Switch>
        </SecureRoute>
      )}
    </Switch>
  );
};

export default AppRoute;
